import { useEffect, useState } from "react"
import { Navigate, useNavigate, useLocation } from "react-router-dom"
import { motion } from "framer-motion"
import {
  Lock, Zap, AlertTriangle, CreditCard,
  Home, FileText, CalendarDays, User,
} from "lucide-react"
import { useSubscription } from "@/hooks/useSubscription"
import { getStripePrice, type StripePriceRow, type SubscriptionStatus } from "@/lib/supabase"

// ── Bottom nav (identique aux pages dashboard) ────────────────────

const NAV_ITEMS = [
  { id: "home",     label: "Accueil",    Icon: Home,         path: "/dashboard" },
  { id: "sessions", label: "Séances",    Icon: FileText,     path: "/dashboard/sessions" },
  { id: "calendar", label: "Calendrier", Icon: CalendarDays, path: "/dashboard/calendar" },
  { id: "profile",  label: "Profil",     Icon: User,         path: "/dashboard/profile" },
]

// ── Config par statut ─────────────────────────────────────────────

type StatusConfig = {
  IconComponent: React.FC<{ className?: string }>
  badge: string
  color: string
  title: string
  body: string
  ctaLabel: string
  usePortal: boolean
}

/**
 * Le montant n'est plus écrit en dur : il vient de `stripe_prices`, comme dans
 * les Réglages et la page de facturation. Trois autres formules arrivent, et
 * l'ancien texte annonçait « 29,99 € » et « Coaching Évolutif » — un nom d'offre
 * qui n'existe plus — sur tous les écrans bloqués.
 */
function priceSuffix(price: StripePriceRow | null): string {
  if (!price) return ""
  const amount = (price.amount_cents / 100).toLocaleString("fr-FR", {
    minimumFractionDigits: price.amount_cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })
  const unit = price.interval === "year" ? "an" : "mois"
  return ` — ${amount} €/${unit}`
}

function getStatusConfig(status: SubscriptionStatus | null, price: StripePriceRow | null): StatusConfig {
  const planName = price?.product_name ?? "un accompagnement"
  if (status === "past_due") return {
    IconComponent: CreditCard,
    badge: "Paiement en attente",
    color: "#f97316",
    title: "Ton paiement a échoué",
    body: "Mets à jour ton moyen de paiement pour retrouver l'accès complet à ton coaching.",
    ctaLabel: "Mettre à jour mon paiement",
    usePortal: true,
  }
  if (status === "canceled") return {
    IconComponent: Zap,
    badge: "Abonnement résilié",
    color: "#94a3b8",
    title: "Ton abonnement est résilié",
    body: "Réactive ton accompagnement pour accéder à ton programme, ton monitoring et tes plans nutrition.",
    ctaLabel: `Réactiver${priceSuffix(price)}`,
    usePortal: false,
  }
  if (status === "incomplete") return {
    IconComponent: AlertTriangle,
    badge: "Activation incomplète",
    color: "#f97316",
    title: "Ton abonnement n'a pas pu être activé",
    body: "Finalise ton paiement pour débloquer toutes les fonctionnalités.",
    ctaLabel: "Finaliser mon abonnement",
    usePortal: false,
  }
  if (status === "trialing") return {
    // Chemin de repli : un essai terminé est normalement redirigé vers /fin-essai
    // (bilan des 7 jours, puis les 4 offres) avant d'arriver ici.
    IconComponent: Lock,
    badge: "Semaine d'essai terminée",
    color: "#06b6d4",
    title: "Ta semaine d'essai est terminée",
    body: "Choisis ton accompagnement pour reprendre ton programme, ton monitoring et ta nutrition là où tu les as laissés.",
    ctaLabel: `Continuer${priceSuffix(price)}`,
    usePortal: false,
  }
  // status === null : aucune ligne d'abonnement
  return {
    IconComponent: Lock,
    badge: "Accès réservé",
    color: "#06b6d4",
    title: `Cette fonctionnalité demande ${planName}`,
    body: "Programme adaptatif, monitoring scientifique et plans nutrition personnalisés.",
    ctaLabel: `Découvrir les offres${priceSuffix(price)}`,
    usePortal: false,
  }
}

// ── UpgradePrompt ─────────────────────────────────────────────────

interface UpgradePromptProps {
  status: SubscriptionStatus | null
  price: StripePriceRow | null
  feature?: string
  onCheckout: () => Promise<void>
  onPortal: () => Promise<void>
}

function UpgradePrompt({ status, price, feature, onCheckout, onPortal }: UpgradePromptProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [ctaLoading, setCtaLoading] = useState(false)

  const config = getStatusConfig(status, price)
  const { IconComponent, badge, color, title, body, ctaLabel, usePortal } = config

  const handleCta = async () => {
    setCtaLoading(true)
    try {
      if (usePortal) await onPortal()
      else await onCheckout()
    } catch (err) {
      console.error("[UpgradePrompt] cta error:", err)
    } finally {
      setCtaLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#060a13" }}>
      {/* Contenu principal centré */}
      <div className="flex-1 flex flex-col items-center justify-center px-5 pb-24 pt-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="w-full max-w-sm"
        >
          <div
            className="rounded-2xl p-6 text-center"
            style={{
              background: "rgba(12,20,35,0.75)",
              border: "1px solid rgba(30,41,59,0.55)",
            }}
          >
            {/* Icône */}
            <div
              className="mx-auto mb-4 w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{
                background: `${color}15`,
                border: `1px solid ${color}30`,
              }}
            >
              <span style={{ color }}>
                <IconComponent className="w-7 h-7" />
              </span>
            </div>

            {/* Badge statut */}
            <span
              className="inline-block px-3 py-1 rounded-full text-[11px] font-semibold mb-5"
              style={{ background: `${color}20`, color }}
            >
              {badge}
            </span>

            {/* Nom de la fonctionnalité bloquée */}
            {feature && (
              <p
                className="text-[11px] font-medium mb-2"
                style={{ color: "#64748b" }}
              >
                🔒 {feature}
              </p>
            )}

            {/* Titre */}
            <h2 className="text-white text-[18px] font-bold leading-snug mb-3">
              {title}
            </h2>

            {/* Corps */}
            <p className="text-sm leading-relaxed mb-6" style={{ color: "#94a3b8" }}>
              {body}
            </p>

            {/* CTA principal */}
            <motion.button
              onClick={handleCta}
              disabled={ctaLoading}
              whileTap={ctaLoading ? {} : { scale: 0.97 }}
              animate={ctaLoading ? {} : { boxShadow: ["0 0 16px rgba(6,182,212,0.3)", "0 0 28px rgba(6,182,212,0.5)", "0 0 16px rgba(6,182,212,0.3)"] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-full py-3.5 rounded-xl font-semibold text-sm cursor-pointer"
              style={{
                background: ctaLoading
                  ? "rgba(6,182,212,0.4)"
                  : "linear-gradient(135deg, #06b6d4, #0891b2)",
                color: "#fff",
              }}
            >
              {ctaLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                  Chargement…
                </span>
              ) : ctaLabel}
            </motion.button>

            {/* Lien secondaire */}
            <button
              onClick={() => navigate("/dashboard/billing")}
              className="mt-4 text-[12px] cursor-pointer transition-colors duration-150"
              style={{ color: "#64748b" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#94a3b8")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#64748b")}
            >
              Voir mon statut d'abonnement →
            </button>
          </div>
        </motion.div>
      </div>

      {/* Bottom nav — toujours accessible même depuis une page bloquée */}
      <nav
        className="fixed bottom-0 left-0 right-0 flex"
        style={{
          background: "rgba(6,10,19,0.95)",
          borderTop: "1px solid rgba(30,41,59,0.55)",
          backdropFilter: "blur(12px)",
        }}
      >
        {NAV_ITEMS.map(({ id, label, Icon: NavIcon, path }) => {
          const active =
            location.pathname === path ||
            (id !== "home" && location.pathname.startsWith(path))
          return (
            <button
              key={id}
              onClick={() => navigate(path)}
              className="flex-1 flex flex-col items-center gap-1 py-3 cursor-pointer transition-colors duration-150"
              style={{ color: active ? "#06b6d4" : "#64748b" }}
            >
              <NavIcon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{label}</span>
            </button>
          )
        })}
      </nav>
    </div>
  )
}

// ── RequireSubscription ───────────────────────────────────────────

interface RequireSubscriptionProps {
  children: React.ReactNode
  feature?: string
}

export function RequireSubscription({ children, feature }: RequireSubscriptionProps) {
  const {
    isActive, status, trialEnded, subscription, loading, startCheckout, openCustomerPortal,
  } = useSubscription()
  const [price, setPrice] = useState<StripePriceRow | null>(null)

  useEffect(() => {
    if (!subscription?.price_id) return
    getStripePrice(subscription.price_id).then(setPrice).catch(() => null)
  }, [subscription?.price_id])

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#060a13" }}
      >
        <div className="w-8 h-8 rounded-full border-2 border-cyan-500/20 border-t-cyan-400 animate-spin" />
      </div>
    )
  }

  // Fin de la semaine d'essai : le client ne tombe plus sur un mur d'abonnement
  // générique, il reçoit son bilan des 7 jours puis les 4 offres (D4, D7).
  if (trialEnded) {
    return <Navigate to="/fin-essai" replace />
  }

  if (!isActive) {
    return (
      <UpgradePrompt
        status={status}
        price={price}
        feature={feature}
        onCheckout={startCheckout}
        onPortal={openCustomerPortal}
      />
    )
  }

  return <>{children}</>
}
